import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import MathText from "@/components/MathText";
import { BookOpen, Dumbbell, Layers, Flame, Target, Sparkles, ArrowRight, ClipboardCheck } from "lucide-react";

export default function Today() {
  const { studentId } = useOutletContext();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/students/${studentId}/today`).then(({ data }) => setData(data)); }, [studentId]);
  if (!data) return <Spinner label="Building today's plan" />;

  if (data.diagnostic_needed) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="p-8 text-center" data-testid="diagnostic-cta">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[var(--ns-primary-light)] text-[var(--ns-primary)] grid place-items-center mb-4">
            <ClipboardCheck size={28} />
          </div>
          <h1 className="font-head text-2xl font-bold">Let's find your starting point</h1>
          <p className="text-[var(--ns-muted)] mt-2 max-w-sm mx-auto">A short adaptive check maps exactly what you know and what to work on first. No pressure — just answer what you can.</p>
          <Button className="mt-6 mx-auto px-7" data-testid="start-diagnostic-btn" onClick={() => nav(`/student/${studentId}/diagnostic`)}>Start diagnostic <ArrowRight size={18} /></Button>
        </Card>
      </motion.div>
    );
  }

  const cards = [];
  if (data.lesson) cards.push({
    key: "lesson", icon: BookOpen, tone: "primary", eyebrow: "Learn", title: data.lesson.name,
    body: <MathText text={data.lesson.why_it_matters} />, cta: "Start lesson",
    onClick: () => nav(`/student/${studentId}/lesson/${data.lesson.skill_id}`), testid: "today-lesson-card",
  });
  if (data.practice) cards.push({
    key: "practice", icon: Dumbbell, tone: "gold", eyebrow: "Practice", title: `Practice ${data.practice.name}`,
    body: "A focused practice set to build fluency before your mastery check.", cta: "Practice now",
    onClick: () => nav(`/student/${studentId}/practice/${data.practice.skill_id}`), testid: "today-practice-card",
  });
  const reviewCount = data.review.due_flashcards + data.review.retention_checks.length;
  cards.push({
    key: "review", icon: Layers, tone: "sage", eyebrow: "Review", title: reviewCount ? `${reviewCount} items due` : "Nothing due — nice!",
    body: reviewCount ? "Spaced review keeps mastered skills from slipping." : "Come back tomorrow for spaced review.", cta: "Open review",
    onClick: () => nav(`/student/${studentId}/review`), testid: "today-review-card",
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-head text-3xl font-bold">Today</h1>
          <p className="text-[var(--ns-muted)]">About {data.time_budget} minutes · one focused loop.</p>
        </div>
        <div className="flex gap-2">
          <Pill tone="gold"><Flame size={14} /> {data.streak}</Pill>
        </div>
      </div>

      <Card className="p-4 mb-5 flex items-center gap-4" data-testid="progress-banner">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm text-[var(--ns-muted)]"><Target size={16} /> Next milestone</div>
          <div className="font-head font-semibold text-[var(--ns-text)] mt-0.5">{data.next_milestone}</div>
        </div>
        <div className="text-right">
          <div className="font-head text-2xl font-bold text-[var(--ns-primary)]">{data.progress.percent}%</div>
          <div className="text-xs text-[var(--ns-subtle)]">{data.progress.mastered}/{data.progress.total} mastered</div>
        </div>
      </Card>

      <div className="grid gap-4">
        {cards.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
            <Card className="p-5 flex items-start gap-4" data-testid={c.testid}>
              <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0"
                style={{ background: "var(--ns-primary-light)", color: "var(--ns-primary)" }}>
                <c.icon size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)]">{c.eyebrow}</div>
                <h3 className="font-head text-lg font-bold text-[var(--ns-text)] mt-0.5">{c.title}</h3>
                <div className="text-sm text-[var(--ns-muted)] mt-1">{c.body}</div>
                <Button variant="subtle" className="mt-3" onClick={c.onClick}>{c.cta} <ArrowRight size={16} /></Button>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {data.lesson && (
        <Card className="p-4 mt-4 flex items-center justify-between" data-testid="mastery-shortcut">
          <div className="flex items-center gap-2 text-sm"><Sparkles size={18} className="text-[var(--ns-gold)]" /> Feeling confident? Take the mastery check.</div>
          <Button variant="outline" onClick={() => nav(`/student/${studentId}/mastery/${data.lesson.skill_id}`)}>Mastery</Button>
        </Card>
      )}
    </div>
  );
}
